/** @jsxImportSource @emotion/react */
import AccordionArrowDownIcon from '@app/assets/icons/AccordionArrowDownIcon'
import { OpenInNew } from '@mui/icons-material'
import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grow,
  Typography,
  useTheme,
} from '@mui/material'
import { MapLayer } from '@app/redux/riskMap/riskMapSlice'
import { RootState } from '@app/redux/store'
import { RiskSummaryReportButton } from '@app/components/Molecules/Reports/RiskSummary'
import { ExportRiskSummaryGeoJsonButton } from '@app/components/Molecules/Reports/RiskSummary/ExportRiskSummaryGeoJson'
import { ExportRiskSummaryXlsxButton } from '@app/components/Molecules/Reports/RiskSummary/ExportRiskSummaryXlxs'
import { printButton } from '@app/components/Molecules/Reports/RiskSummary/RiskSummaryReport.styles'
import { SelectedElementReportButton } from '@app/components/Molecules/Reports/SelectedElement'
import { Tabs } from '@app/components/Molecules/Tabs'
import { clientConfigAtom } from '@app/state/clientsAtom'
import { infoLayerFocusAtom, RiskSideDrawerLayerTabs } from '@app/state/layerAtom'
import { useAtom, useAtomValue } from 'jotai'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { canExportAtom } from '@app/hooks/useUserProfile'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import { FEATURE_FLAGS } from '@app/app-constants'

import {
  exportMenuStyles,
  infoPanelFooter,
  infoPanelTabs,
  informativePanel,
  tabContent,
  tabsContainer,
} from './InformativePanel.styles'
import { LayersTab } from './LayersTab'
import { RiskSummary } from './RiskSummaryTab'
import { SelectedTab } from './SelectedTab'
import { RiskTable } from './RiskTableTab/RiskTable'
import { useLiveContext } from '@app/modules/Live/contexts/Live.Context'

export type HazardGroup = {
  hazard_type: string
  scenarios: {
    scenario: string
    layer: MapLayer
  }[]
}

export type GroupedLayers = {
  hazards: HazardGroup[]
  assets: MapLayer[]
  information: MapLayer[]
}

function groupHazardsByType(hazardLayers: MapLayer[]): HazardGroup[] {
  const hazardLayersByType: HazardGroup[] = []
  hazardLayers.forEach((readonly_layer) => {
    const layer = { ...readonly_layer }
    const hazardType = layer.hazard_title ?? layer.display_name.split('(')[0].trim()
    let hazardScenario = layer.display_name
    if (layer.scenario_name) {
      hazardScenario = layer.scenario_name
    } else if (hazardScenario.includes('(')) {
      hazardScenario = hazardScenario.split('(')[1].split(')')[0].trim()
      layer.scenario_name = hazardScenario
    }
    const hazardGroup = hazardLayersByType.find((group) => group.hazard_type === hazardType)
    if (hazardGroup) {
      hazardGroup.scenarios.push({ scenario: hazardScenario, layer })
    } else {
      hazardLayersByType.push({
        hazard_type: hazardType,
        scenarios: [{ scenario: hazardScenario, layer }],
      })
    }
  })
  return hazardLayersByType
}

export const InformativePanel = () => {
  const theme = useTheme()
  const { useFeatureFlag } = useFeatureFlags()
  const { liveMapSelected } = useLiveContext()
  const canExport = useAtomValue(canExportAtom)
  const isRiskTableEnabled = useFeatureFlag(FEATURE_FLAGS.RISKTABLE)
  const [infoLayerFocus, setInfoLayerFocus] = useAtom(infoLayerFocusAtom)
  const methods_report_url = useAtomValue(clientConfigAtom)?.methods_report_url
  // const { infoLayerFocus } = useSelector((state: RootState) => state.sideDrawer)
  const { selectedLayer: selectedAsset, layers } = useSelector((state: RootState) => state.riskMap)

  const groupedLayers = useMemo<GroupedLayers>(() => {
    return {
      hazards: groupHazardsByType(layers.filter((layer) => layer.layerType === 'hazard')),
      assets: layers.filter((layer) => layer.layerType === 'asset'),
      information: layers.filter((layer) => layer.layerType === 'information'),
    }
  }, [layers])

  // Stop the selection tab showing when Risk Table's Show on map clicked. Turns out was a tricky thing to do - KP
  // Claude:
  // Keep a ref to the current focus so the selectedAsset effect can read it
  // without adding it to deps (which would incorrectly re-navigate on manual tab clicks).
  const infoLayerFocusRef = useRef(infoLayerFocus)
  infoLayerFocusRef.current = infoLayerFocus

  useEffect(() => {
    if (selectedAsset && infoLayerFocusRef.current !== 'risk_table') {
      setInfoLayerFocus('selection')
    }
  }, [selectedAsset, setInfoLayerFocus])

  const currentTabIndex = useMemo(() => {
    if (liveMapSelected)
      switch (infoLayerFocus) {
        case 'layers':
          return 0
        case 'selection':
          return 1
        default:
          return 0
      }
    else
      switch (infoLayerFocus) {
        case 'layers':
          return 0
        case 'risk':
          return 1
        case 'selection':
          return 2
        case 'risk_table':
          return 3
        default:
          return 0
      }
  }, [infoLayerFocus, liveMapSelected])

  const tabs = liveMapSelected
    ? [
        {
          label: 'Layers',
          content: (
            <Box css={tabContent}>
              <LayersTab />
            </Box>
          ),
          value: 'layers',
        },
        {
          label: 'Selected',
          content: (
            <Box css={tabContent}>
              <SelectedTab groupedLayers={groupedLayers} />
            </Box>
          ),
          value: 'selection',
        },
      ]
    : [
        {
          label: 'Layers',
          content: (
            <Box css={tabContent}>
              <LayersTab />
            </Box>
          ),
          value: 'layers',
        },
        {
          label: 'Risk Summary',
          content: (
            <Box css={tabContent}>
              <RiskSummary groupedLayers={groupedLayers} />
            </Box>
          ),
          value: 'risk',
        },
        {
          label: 'Selected',
          content: (
            <Box css={tabContent}>
              <SelectedTab groupedLayers={groupedLayers} />
            </Box>
          ),
          value: 'selection',
        },
        ...(isRiskTableEnabled
          ? [
              {
                label: 'Risk Table',
                content: (
                  <Box css={tabContent}>
                    <RiskTable />
                  </Box>
                ),
                value: 'risk_table',
              },
            ]
          : []),
      ]

  const handleLayerFocusChange = (newValue: number) => {
    const value = tabs[newValue].value as RiskSideDrawerLayerTabs
    setInfoLayerFocus(value)
  }

  const footerContent = []
  const exportMenuItems: ReactNode[] = []
  if (methods_report_url && infoLayerFocus === 'risk') {
    footerContent.push(
      <Link
        to={methods_report_url}
        target="_blank"
        style={{ width: '100%', textDecoration: 'none' }}
        className="tutorial-methodology-button"
        key="tutorialMethodology"
      >
        <Button css={printButton({ theme })} endIcon={<OpenInNew />}>
          Methodology Report
        </Button>
      </Link>,
    )
  }
  if (
    infoLayerFocus === 'risk' &&
    layers.some((layer) => layer.layerType === 'hazard') &&
    layers.some((layer) => layer.layerType === 'asset')
  ) {
    exportMenuItems.push(<RiskSummaryReportButton key="risksummaryreport" />)
    exportMenuItems.push(
      <ExportRiskSummaryXlsxButton
        key="exportriskxlsx"
        onReportTooLarge={() => setReportTooLargeOpen(true)}
      />,
    )
    exportMenuItems.push(<ExportRiskSummaryGeoJsonButton key="exportriskgeojson" />)
  } else if (infoLayerFocus === 'selection' && selectedAsset && canExport && !liveMapSelected) {
    footerContent.push(<SelectedElementReportButton key="selectedElementReport" />)
  }

  const [showExportMenu, setShowExportMenu] = useState(false)
  const [reportTooLargeOpen, setReportTooLargeOpen] = useState(false)

  return (
    <Box css={informativePanel}>
      <Box css={tabsContainer}>
        <Tabs
          css={infoPanelTabs({ theme })}
          handleChangeTab={handleLayerFocusChange}
          tabs={tabs}
          tabIndex={currentTabIndex}
        />
      </Box>
      {!!footerContent.length && (
        <Box css={infoPanelFooter({ theme })}>{footerContent.map((content) => content)}</Box>
      )}

      {!!exportMenuItems.length && canExport && (
        <Box sx={{ position: 'relative' }}>
          <Button
            sx={{
              width: '100%',
              svg: {
                transform: showExportMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.14s',
              },
            }}
            onClick={() => {
              setShowExportMenu((prev) => !prev)
            }}
          >
            Export Risk Summary
            <AccordionArrowDownIcon height="16px" />
          </Button>
          <Grow
            in={showExportMenu}
            style={{ transformOrigin: '50% 100% 0' }}
            mountOnEnter
            unmountOnExit
            timeout={140}
          >
            <Box css={exportMenuStyles({ theme })}>
              <ClickAwayListener onClickAway={() => setShowExportMenu(false)}>
                <Box
                  onClick={() => setShowExportMenu(false)}
                  className="tutorial-summary-report-button"
                >
                  {...exportMenuItems}
                </Box>
              </ClickAwayListener>
            </Box>
          </Grow>
        </Box>
      )}
      <Box className="tutorial-sidedrawer" />

      <Dialog open={reportTooLargeOpen} onClose={() => setReportTooLargeOpen(false)}>
        <DialogTitle>Unable to process report</DialogTitle>
        <DialogContent>
          <Typography>
            The combination of elements and risk sources selected would generate a spreadsheet with
            over 100,000 rows. Please either reduce the number of elements or risk sources selected
            or restrict the area of the report to a smaller region.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportTooLargeOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
